import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

export interface Folder {
  id: string;
  name: string;
  folder: string;
  icon?: string;
  color?: string;
  documentCount?: number;
  isSystem?: boolean;
}

export interface FoldersState {
  byId: { [folderId: string]: Folder };
  allIds: string[];
  selectedFolderId: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: FoldersState = {
  byId: {},
  allIds: [],
  selectedFolderId: null,
  isLoading: false,
  error: null,
};

const foldersSlice = createSlice({
  name: 'folders',
  initialState,
  reducers: {
    setFolders: (state, action: PayloadAction<Folder[]>) => {
      state.byId = {};
      state.allIds = [];
      action.payload.forEach(folder => {
        state.byId[folder.id] = folder;
        state.allIds.push(folder.id);
      });
    },
    setSelectedFolder: (state, action: PayloadAction<string>) => {
      state.selectedFolderId = action.payload;
    },
    updateFolderDocumentCount: (
      state,
      action: PayloadAction<{ folderId: string; count: number }>,
    ) => {
      if (state.byId[action.payload.folderId]) {
        state.byId[action.payload.folderId].documentCount =
          action.payload.count;
      }
    },
    renameFolder: (
      state,
      action: PayloadAction<{ folderId: string; newName: string }>,
    ) => {
      if (state.byId[action.payload.folderId]) {
        state.byId[action.payload.folderId].name = action.payload.newName;
      }
    },
    deleteFolder: (state, action: PayloadAction<string>) => {
      delete state.byId[action.payload];
      state.allIds = state.allIds.filter(id => id !== action.payload);
      if (state.selectedFolderId === action.payload) {
        state.selectedFolderId = state.allIds[0] || null;
      }
    },
    reorderFolders: (state, action: PayloadAction<string[]>) => {
      state.allIds = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    addFolder: (state, action: PayloadAction<Folder>) => {
      state.byId[action.payload.id] = action.payload;
      state.allIds.push(action.payload.id);
    },

    updateDocumentCounts: (
      state,
      action: PayloadAction<{
        sourceFolderId?: string;
        targetFolderId: string;
        documentCount: number;
      }>,
    ) => {
      const { sourceFolderId, targetFolderId, documentCount } = action.payload;

      // Decrease count from source folder
      if (sourceFolderId && state.byId[sourceFolderId]) {
        state.byId[sourceFolderId].documentCount = Math.max(
          0,
          (state.byId[sourceFolderId].documentCount || 0) - documentCount,
        );
      }

      // Increase count in target folder
      if (state.byId[targetFolderId]) {
        state.byId[targetFolderId].documentCount =
          (state.byId[targetFolderId].documentCount || 0) + documentCount;
      }
    },
  },
});

// Selectors
export const selectFoldersState = (state: any) => state.folders;

export const selectAllFolders = createSelector(
  [selectFoldersState],
  foldersState =>
    foldersState.allIds.map((id: string) => foldersState.byId[id]),
);

export const selectSelectedFolder = createSelector(
  [selectFoldersState],
  foldersState =>
    foldersState.selectedFolderId
      ? foldersState.byId[foldersState.selectedFolderId]
      : null,
);

export const selectFolderById = createSelector(
  [selectFoldersState, (_: any, folderId: string) => folderId],
  (foldersState, folderId) => foldersState.byId[folderId],
);

export const { actions: foldersActions } = foldersSlice;
export default foldersSlice.reducer;
